import { useMemo } from "react";
import ReactFlow, {
    Background,
    Controls,
    type Edge,
    Handle,
    MiniMap,
    type Node,
    type NodeProps,
    Position,
} from "reactflow";
import "reactflow/dist/style.css";
import { buildGraph, type StatusNode } from "./graph";
import type { StatusMeta, Transition, WorkflowDefinition } from "./types";

/** Node renderer: a status box, flagged when nothing leaves it. */
function StatusNodeView({ data }: NodeProps<StatusNode["data"]>) {
    const isDeadEnd = data.outgoing === 0;

    return (
        <div
            className={`aer-wf-node${isDeadEnd ? " aer-wf-node--dead-end" : ""}`}
            title={
                isDeadEnd
                    ? `${data.status} has no transition out of it`
                    : `${data.status}: ${data.outgoing} out, ${data.incoming} in`
            }
        >
            {/* Top and bottom, not left and right: the flow is laid out top to bottom, and
                side handles would route every edge sideways around the nodes. */}
            <Handle type="target" position={Position.Top} />
            <span className="aer-wf-node__status">{data.status}</span>
            {isDeadEnd && (
                <span className="aer-wf-node__flag" aria-hidden="true">
                    ⚠
                </span>
            )}
            <Handle type="source" position={Position.Bottom} />
        </div>
    );
}

const nodeTypes = { status: StatusNodeView };

interface StatusGraphProps {
    definition: WorkflowDefinition;
    statuses: StatusMeta[];
    selectedTransitionId: string | null;
    onSelectTransition: (transition: Transition) => void;
    /**
     * Called when the user drags a connection between two statuses. Undefined leaves the graph
     * read-only, which is also what happens while a create is in flight.
     */
    onConnectStatuses?: (fromStatus: string, toStatus: string) => void;
}

/**
 * The status-flow graph.
 *
 * Dragging from one status to another creates a transition; everything else about the definition
 * is edited in the detail panel. Node dragging stays disabled — the layout is derived from status
 * rank, so a moved node would snap back on the next render and imply a persistence that does not
 * exist.
 *
 * Named StatusGraph rather than Graph so this file cannot be confused with graph.ts, which
 * holds the layout logic — the two resolve to the same module on a case-insensitive filesystem.
 */
export function StatusGraph({
    definition,
    statuses,
    selectedTransitionId,
    onSelectTransition,
    onConnectStatuses,
}: StatusGraphProps) {
    const { nodes, edges } = useMemo(
        () => buildGraph(definition, statuses),
        [definition, statuses],
    );

    const flowEdges: Edge[] = useMemo(
        () =>
            edges.map((edge) => ({
                id: edge.id,
                source: edge.source,
                target: edge.target,
                label: edge.label,
                animated: edge.animated,
                // MANUAL transitions are fired by a person; dashing them keeps the distinction
                // visible without relying on colour alone.
                style: edge.animated ? { strokeDasharray: "6 4" } : undefined,
                className:
                    edge.id === selectedTransitionId
                        ? "aer-wf-edge--selected"
                        : undefined,
                data: edge.data,
            })),
        [edges, selectedTransitionId],
    );

    return (
        <div className="aer-wf-graph">
            <ReactFlow
                nodes={nodes as unknown as Node[]}
                edges={flowEdges}
                nodeTypes={nodeTypes}
                onEdgeClick={(_event, edge) => {
                    const transition = (
                        edge.data as { transition?: Transition } | undefined
                    )?.transition;
                    if (transition) {
                        onSelectTransition(transition);
                    }
                }}
                onConnect={(connection) => {
                    // Node ids are the status names, so a connection is already the pair the
                    // API needs. The server rejects a self-transition and a duplicate pair.
                    if (
                        onConnectStatuses &&
                        connection.source &&
                        connection.target
                    ) {
                        onConnectStatuses(connection.source, connection.target);
                    }
                }}
                nodesDraggable={false}
                nodesConnectable={Boolean(onConnectStatuses)}
                edgesFocusable
                fitView
                proOptions={{ hideAttribution: false }}
            >
                <Background />
                <Controls showInteractive={false} />
                <MiniMap pannable zoomable />
            </ReactFlow>
        </div>
    );
}
